import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { navKey } from './session';

/**
 * P13: what a screen shows (filter, search, open record, form) lives in its address, so "Wstecz / Dalej" and a
 * reload bring back that very place. Changes inside a screen replace the history entry (only moving to another
 * screen — or the full view of an event — is a new step), so typing in a search box never fills the history.
 */

/** Changes not yet seen by a render: several changes in one handler build on one another. */
let pending: { key: string; search: string } | null = null;

export type UrlChanges = Record<string, string | null | undefined | false>;

export function useUrlState() {
  const location = useLocation();
  const navigate = useNavigate();
  // The router applies a new address inside a transition; a controlled field (checkbox, search box) must show the
  // new value at once, or React puts the old one back and the keystroke or click is lost. So the screen reads its
  // own copy, set right away by `update` and following the address whenever it changes in any other way.
  const [mirror, setMirror] = useState({ seen: location.search, search: location.search, mine: [] as string[] });
  let current = mirror;
  if (mirror.seen !== location.search) {
    const at = mirror.mine.indexOf(location.search);
    // our own change arriving (maybe with later ones of ours still on the way): keep the newest; else follow the address
    current = at >= 0 ? { seen: location.search, search: mirror.search, mine: mirror.mine.slice(at + 1) } : { seen: location.search, search: location.search, mine: [] };
    setMirror(current);
  }
  // a render at another history entry: changes made at an older one (before a step) must not be built on
  if (pending && pending.key !== location.key) pending = null;
  const params = useMemo(() => new URLSearchParams(current.search), [current.search]);
  const update = (changes: UrlChanges, opts: { push?: boolean } = {}) => {
    const from = pending?.key === location.key ? pending.search : current.search;
    const next = new URLSearchParams(from);
    for (const [name, value] of Object.entries(changes)) {
      if (value === null || value === undefined || value === false || value === '') next.delete(name);
      else next.set(name, value);
    }
    const qs = next.toString();
    const search = qs ? `?${qs}` : '';
    if (search === from && !opts.push) return;
    pending = { key: location.key, search };
    setMirror((m) => ({ seen: m.seen, search, mine: [...m.mine, search] }));
    navigate({ pathname: location.pathname, search }, { replace: !opts.push });
  };
  return { params, update };
}

/** One text value of the address ('' when absent); setting it replaces the history entry. */
export function useUrlParam(name: string): [string, (value: string) => void] {
  const { params, update } = useUrlState();
  return [params.get(name) ?? '', (value: string) => update({ [name]: value })];
}

/** A yes/no value of the address (`name=1`). */
export function useUrlFlag(name: string): [boolean, (value: boolean) => void] {
  const { params, update } = useUrlState();
  return [params.get(name) === '1', (value: boolean) => update({ [name]: value ? '1' : null })];
}

const scrollKey = () => navKey('nav.scroll');
const KEEP = 100;

function loadScroll(): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(scrollKey()) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function saveScroll(map: Record<string, number>) {
  try {
    const keys = Object.keys(map);
    for (const k of keys.slice(0, Math.max(0, keys.length - KEEP))) delete map[k];
    sessionStorage.setItem(scrollKey(), JSON.stringify(map));
  } catch {
    // storage blocked: the position is kept for this page's life only
  }
}

/**
 * The page's scroll position per history entry: a step back or forward (or a reload) puts the screen where it
 * was — retried for a moment while the list is still loading, until the person scrolls themselves; a new screen
 * starts at the top; a change inside the screen (replace) keeps the position.
 */
export function useScrollRestore() {
  const location = useLocation();
  const type = useNavigationType();
  const positions = useRef<Record<string, number> | null>(null);
  const keyRef = useRef(location.key);
  const first = useRef(true);
  positions.current ??= loadScroll();

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    let frame = 0;
    const onScroll = () => {
      // in memory at once (the next screen may come before the timer), in storage a moment later
      positions.current![keyRef.current] = Math.round(window.scrollY);
      if (frame) return;
      frame = window.setTimeout(() => {
        frame = 0;
        saveScroll(positions.current!);
      }, 200);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(frame);
    };
  }, []);

  // before the browser paints the new screen: a shorter page clamps the scroll, which must not count for the old one
  useLayoutEffect(() => {
    const map = positions.current!;
    const before = keyRef.current;
    keyRef.current = location.key;
    const restoring = first.current || type === 'POP';
    first.current = false;
    if (type === 'REPLACE' && !restoring) {
      // the same screen with another filter: the place stays where it is
      map[location.key] = map[before] ?? Math.round(window.scrollY);
      saveScroll(map);
      return;
    }
    saveScroll(map);
    if (!restoring) {
      window.scrollTo(0, 0);
      return;
    }
    const target = map[location.key];
    if (target === undefined) return;
    let tries = 0;
    let timer = 0;
    const stop = () => {
      window.clearTimeout(timer);
      for (const ev of ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const) window.removeEventListener(ev, stop);
    };
    const attempt = () => {
      window.scrollTo(0, target);
      if (Math.round(window.scrollY) >= target || ++tries > 40) stop();
      else timer = window.setTimeout(attempt, 50);
    };
    for (const ev of ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const) window.addEventListener(ev, stop, { passive: true });
    attempt();
    return stop;
    // a new entry or a replaced one always has a new key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}
